import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/apiFetch";
import { useDebouncedValue } from "./useDebouncedValue";
import { checkUsernameFormat, type UsernameErrorCode } from "../components/helpers/usernameRules";
import type { UsernameAvailabilityResponse } from "../types/Auth";

export type UsernameAvailabilityStatus =
    | "idle"        // nothing typed, or unchanged from `ignore`
    | "invalid"     // fails the shape check locally — no request made
    | "checking"    // debounced request in flight
    | "available"
    | "unavailable" // taken / reserved / profanity (see `code`)
    | "unknown";    // the check itself failed — let the submit decide

export interface UsernameAvailability {
    status: UsernameAvailabilityStatus;
    code: UsernameErrorCode | string | null;
}

interface Options {
    enabled: boolean;
    /** The handle the account already has — typing it back is "no change", not a lookup. */
    ignore?: string | null;
    /** Forwarded as excludeUID so the admin modal doesn't see the user's own handle as taken. */
    excludeUID?: number | null;
}

/** Live "is this handle free?" for the account-setup screen and the Users
 *  modals. Format errors are answered locally and instantly; everything
 *  else is a debounced GET /login/username/available. A failed request
 *  reports `unknown` so the form can still submit — the POST is the gate. */
export const useUsernameAvailability = (username: string, { enabled, ignore = null, excludeUID = null }: Options): UsernameAvailability => {
    const [result, setResult] = useState<UsernameAvailability>({ status: "idle", code: null });
    const debounced = useDebouncedValue(username, 350);
    const seq = useRef(0);

    useEffect(() => {
        seq.current += 1;
        const mySeq = seq.current;
        if (!enabled || username === "" || (ignore != null && username === ignore)) {
            setResult({ status: "idle", code: null });
            return;
        }
        const formatCode = checkUsernameFormat(username);
        if (formatCode) {
            setResult({ status: "invalid", code: formatCode });
            return;
        }
        // Valid shape, waiting for the debounce to settle before asking.
        if (debounced !== username) {
            setResult({ status: "checking", code: null });
            return;
        }
        let cancelled = false;
        setResult({ status: "checking", code: null });
        const query = `u=${encodeURIComponent(username)}${excludeUID != null ? `&excludeUID=${excludeUID}` : ""}`;
        (async () => {
            try {
                const res = await apiFetch(`/login/username/available?${query}`, { method: "GET" });
                const body = (await res?.json?.().catch(() => null)) as UsernameAvailabilityResponse | null;
                if (cancelled || mySeq !== seq.current) return;
                if (res?.ok && body && typeof body.available === "boolean") {
                    setResult(body.available
                        ? { status: "available", code: null }
                        : { status: "unavailable", code: body.code ?? "taken" });
                } else {
                    setResult({ status: "unknown", code: null });
                }
            } catch {
                if (!cancelled && mySeq === seq.current) setResult({ status: "unknown", code: null });
            }
        })();
        return () => { cancelled = true; };
    }, [username, debounced, enabled, ignore, excludeUID]);

    return result;
};
